import { Directive, ElementRef, HostListener, Input, OnChanges, OnDestroy, Renderer2, SimpleChanges } from '@angular/core';
import { NgControl } from '@angular/forms';

@Directive({
  selector: '[appNumericDecimal]'
})
export class NumericDecimalDirective implements OnChanges, OnDestroy {
  @Input('appNumericDecimal') value: any;

  private timeoutId: any;
  private isBlurring: boolean = false;

  constructor(
    private el: ElementRef,
    private renderer: Renderer2,
    private ngControl: NgControl
  ) {}

  ngOnInit(): void {
    this.formatAndSetInputValue(this.value);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['value'] && !this.isBlurring) {
      this.formatAndSetInputValue(changes['value'].currentValue);
    }
  }

  ngOnDestroy(): void {
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
    }
  }

  // Auto-select functionality
  @HostListener('mousedown', ['$event'])
  onMouseDown(event: MouseEvent) {
    event.preventDefault(); // Prevents cursor from jumping to the end
  }

  @HostListener('focus')
  @HostListener('click')
  onFocusOrClick() {
    setTimeout(() => {
      requestAnimationFrame(() => {
        this.selectText();
      });
    }, 0);
  }

  // Format decimal functionality
  @HostListener('input', ['$event'])
  onInput(event: Event): void {
    this.isBlurring = true;
    const inputElement = this.el.nativeElement as HTMLInputElement;
    const currentValue = inputElement.value.trim();
    const cursorPosition = inputElement.selectionStart;
    const formattedValue = this.formatPartialValue(currentValue);

    this.timeoutId = setTimeout(() => {
      if (!inputElement.isConnected) {
        return;
      }

      if (formattedValue !== inputElement.value) {
        this.renderer.setProperty(inputElement, 'value', formattedValue);
        this.restoreCursorPosition(inputElement, formattedValue, cursorPosition);
      }

      if (this.ngControl && this.ngControl.control) {
        this.ngControl.control.setValue(formattedValue, { emitEvent: false });
      }
    });
  }

  @HostListener('blur')
  onBlur(): void {
    const inputElement = this.el.nativeElement as HTMLInputElement;
    const currentValue = inputElement.value.trim();
    const formattedValue = this.formatValue(currentValue);

    this.timeoutId = setTimeout(() => {
      if (!inputElement.isConnected) {
        return;
      }

      if (formattedValue !== inputElement.value) {
        this.renderer.setProperty(inputElement, 'value', formattedValue);
        inputElement.setSelectionRange(formattedValue.length, formattedValue.length);
      }

      if (this.ngControl && this.ngControl.control) {
        this.ngControl.control.setValue(formattedValue, { emitEvent: false });
      }
      this.isBlurring = false;
    });
  }

  @HostListener('keydown', ['$event'])
  onKeyDown(event: KeyboardEvent): void {
    const inputElement = this.el.nativeElement as HTMLInputElement;
    const rawValue = inputElement.value.trim();
    const hasDecimalPoint = rawValue.includes('.');
    const cursorPosition = inputElement.selectionStart;
    
    const currentValue = hasDecimalPoint ? this.formatValue(rawValue) : rawValue;
    const decimalPointIndex = currentValue.indexOf('.');

    // Allow utility keys
    const isUtilityKey = [
      'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown',
      'Backspace', 'Delete', 'Tab', 'End', 'Home', 'Escape',
      'PageUp', 'PageDown', 'Insert', 'Enter', 'Shift', 'Control'
    ].includes(event.key);

    if (isUtilityKey) {
      // Special handling for backspace at decimal point
      if (event.key === 'Backspace') {
        if ((cursorPosition === decimalPointIndex + 1) || (cursorPosition == decimalPointIndex && parseFloat(currentValue) < 1)) {
          event.preventDefault();
          inputElement.setSelectionRange(decimalPointIndex, decimalPointIndex);
        }
      }
      return;
    }

    // Allow copy, paste, cut, select all
    if ((event.ctrlKey || event.metaKey) && ['c', 'v', 'x', 'a'].includes(event.key.toLowerCase())) {
      return;
    }

    // Only allow digits and decimal point
    if (!/[\d.]/.test(event.key)) {
      event.preventDefault();
      return;
    }

    // Prevent multiple decimal points
    if (event.key === '.' && decimalPointIndex !== -1) {
      event.preventDefault();
      return;
    }

    // Prevent negative values
    if (event.key === '-') {
      event.preventDefault();
      return;
    }
  }

  private formatAndSetInputValue(value: any): void {
    const formattedValue = this.formatValue(value);

    this.timeoutId = setTimeout(() => {
      const inputElement = this.el.nativeElement as HTMLInputElement;

      if (!inputElement.isConnected) {
        return;
      }

      if (formattedValue !== inputElement.value) {
        this.renderer.setProperty(inputElement, 'value', formattedValue);
        this.restoreCursorPosition(inputElement, formattedValue, inputElement.selectionStart);
      }

      if (this.ngControl && this.ngControl.control) {
        this.ngControl.control.setValue(formattedValue, { emitEvent: false });
      }
    });
  }

  private formatPartialValue(value: any): string {
    if (!value || isNaN(parseFloat(value))) {
      return '0.00';
    }

    if (value.startsWith('0') && value.length > 1 && value[1] !== '.') {
      value = value.replace(/^0+/, '');
    }
    return value;
  }

  private formatValue(value: any): string {
    if (!value || isNaN(parseFloat(value))) {
      return '0.00';
    }

    const numericValue = parseFloat(value);
    return numericValue.toFixed(2);
  }

  private restoreCursorPosition(inputElement: HTMLInputElement, formattedValue: string, cursorPosition: number | null): void {
    if (cursorPosition === null) {
      return;
    }

    const decimalPointIndex = formattedValue.indexOf('.');
    let newCursorPosition = cursorPosition;

    if ((newCursorPosition - 1) == decimalPointIndex) {
      newCursorPosition = newCursorPosition - 1;
    } else if ((newCursorPosition + 1) == decimalPointIndex) {
      newCursorPosition = newCursorPosition + 2;
    }

    inputElement.setSelectionRange(newCursorPosition, newCursorPosition);
  }

  private selectText() {
    const input = this.el.nativeElement as HTMLInputElement;
    input.select();
  }
} 